import { useCallback, useEffect, useState } from "react";
import { ROLE, TIP_TYPE } from "../../../../shared/constants/common.js";
import PurchaseCreditsModal from "../../../Modals/Message/PurchaseCreditsModal";
import CallRequestModal from "../call/CallRequestModal";
import CreatorCallRateModal from "../call/CreatorCallRateModal";
import TipModal from "../../../Modals/TipModal";
import UploadContent from "../../../Modals/UploadContent";
import SubscribeModalLoggedIn from "../../../Modals/SubscribeModal/SubscribeModalLoggedIn";
import ChatUnlockForCallModal from "./ChatUnlockForCallModal";
import { captureChatException } from "shared/sentry";

const ChatActions = ({
  userRole,
  roomId,
  otherUserId,
  creatorInfo,
  onTipSuccessInChat,
  isEmptyChat = false,
  openTipModalRef,
  /** Optional ref: `{ handleCallAction(kind) }` for missed-call "Call Back" in the thread. */
  callActionRef,
  onUploadSuccessInChat,
  /** Subscriber: false when chat is locked until follow/subscribe again */
  isSubscribed = true,
  onRefreshRoomInfo,
}) => {
  const isUser = userRole === ROLE.USER;

  const [showCallRequestModal, setShowCallRequestModal] = useState(false);
  const [showCreatorCallRateModal, setShowCreatorCallRateModal] = useState(false);
  /** Which call action opened the request modal (subscriber flow only). */
  const [callRequestKind, setCallRequestKind] = useState('voice');
  const [showPurchaseCreditsModal, setShowPurchaseCreditsModal] = useState(false);
  const [showTipModal, setShowTipModal] = useState(false);
  const [showUnlockForCallModal, setShowUnlockForCallModal] = useState(false);
  /** Which call action opened the unlock modal (voice vs video) — drives header icon in ChatUnlockForCallModal. */
  const [unlockForCallKind, setUnlockForCallKind] = useState('voice');
  const [subscribeFromChatOpenSignal, setSubscribeFromChatOpenSignal] = useState(0);
  const [isRefreshingCallInfo, setIsRefreshingCallInfo] = useState(false);
  const [isCallRequestLoading, setIsCallRequestLoading] = useState(false);

  useEffect(() => {
    if (openTipModalRef) {
      if (!openTipModalRef.current || typeof openTipModalRef.current !== 'object') {
        openTipModalRef.current = {};
      }
      openTipModalRef.current.open = () => setShowTipModal(true);
      return () => {
        if (openTipModalRef.current) openTipModalRef.current.open = null;
      };
    }
  }, [openTipModalRef]);

  const handleCallAction = useCallback(
    async (kind) => {
      if (isRefreshingCallInfo) return;
      const shouldOpenCallRequestModal = isUser;
      if (shouldOpenCallRequestModal) {
        setCallRequestKind(kind);
        setShowCallRequestModal(true);
        setIsCallRequestLoading(true);
      }
      setIsRefreshingCallInfo(true);
      try {
        let refreshResult = null;
        if (typeof onRefreshRoomInfo === "function") {
          try {
            refreshResult = await onRefreshRoomInfo({ force: true, background: true });
          } catch (error) {
            captureChatException(error, {
              tags: { feature: "chat", action: "refresh-before-call-action" },
            });
            return;
          }
        }
        const canOpenCallModal = !refreshResult || refreshResult?.success !== false;
        if (!canOpenCallModal) {
          if (shouldOpenCallRequestModal) {
            setShowCallRequestModal(false);
          }
          return;
        }

        if (isUser && !isSubscribed) {
          setShowCallRequestModal(false);
          setUnlockForCallKind(kind);
          setShowUnlockForCallModal(true);
          return;
        }
        if (isUser) {
          setCallRequestKind(kind);
          setShowCallRequestModal(true);
          return;
        }
        setCallRequestKind(kind);
        setShowCreatorCallRateModal(true);
      } finally {
        setIsCallRequestLoading(false);
        setIsRefreshingCallInfo(false);
      }
    },
    [isUser, isSubscribed, onRefreshRoomInfo, isRefreshingCallInfo]
  );

  useEffect(() => {
    if (!callActionRef) return;
    if (!callActionRef.current || typeof callActionRef.current !== 'object') {
      callActionRef.current = {};
    }
    callActionRef.current.handleCallAction = handleCallAction;
    return () => {
      if (callActionRef.current) callActionRef.current.handleCallAction = null;
    };
  }, [callActionRef, handleCallAction]);

  const handleVoiceCall = useCallback(
    (e) => {
      e?.preventDefault?.();
      e?.stopPropagation?.();
      void handleCallAction('voice');
    },
    [handleCallAction]
  );

  const handleVideoCall = useCallback(
    (e) => {
      e?.preventDefault?.();
      e?.stopPropagation?.();
      void handleCallAction('video');
    },
    [handleCallAction]
  );

  return (
    <>
      <div className="chat_actions" role="toolbar" aria-label="Chat actions">
        {isUser ? (
          <>
            <span
              role="button"
              tabIndex={0}
              onClick={() => setShowPurchaseCreditsModal(true)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setShowPurchaseCreditsModal(true);
                }
              }}
              aria-label="Purchase credits"
            >
              <i className="fa-solid fa-coins" aria-hidden />
            </span>

            {!isEmptyChat && (
              <>
                <span
                  role="button"
                  tabIndex={0}
                  onClick={() => creatorInfo && setShowTipModal(true)}
                  onKeyDown={(e) => {
                    if ((e.key === "Enter" || e.key === " ") && creatorInfo) {
                      e.preventDefault();
                      setShowTipModal(true);
                    }
                  }}
                  aria-label="Pay"
                  title="Pay"
                >
                  <i className="fa-solid fa-dollar-sign" aria-hidden />
                </span>
              </>
            )}
          </>
        ) : (
          <>
            <UploadContent
              forCreatorMessaging
              purposeDest={otherUserId}
              onUploadSuccess={onUploadSuccessInChat}
              triggerElement={
                <span
                  role="button"
                  tabIndex={0}
                  aria-label="Attach"
                  title="Attach"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.currentTarget.click();
                    }
                  }}
                >
                  <i className="fa-solid fa-paperclip" aria-hidden />
                </span>
              }
            />

          </>
        )}

        <span
          role="button"
          tabIndex={0}
          onClick={(e) => handleVoiceCall(e)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              e.stopPropagation();
              handleVoiceCall(e);
            }
          }}
          aria-label="Voice call"
        >
          <i className="fa-solid fa-phone" aria-hidden />
        </span>

        <span
          role="button"
          tabIndex={0}
          onClick={(e) => handleVideoCall(e)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              e.stopPropagation();
              handleVideoCall(e);
            }
          }}
          aria-label="Video call"
        >
          <i className="fa-solid fa-video" aria-hidden />
        </span>
      </div>

      <ChatUnlockForCallModal
        isOpen={showUnlockForCallModal}
        onClose={() => setShowUnlockForCallModal(false)}
        callKind={unlockForCallKind}
        creatorInfo={creatorInfo}
        onFollowSuccess={onRefreshRoomInfo}
        onSubscribeClick={() => setSubscribeFromChatOpenSignal((n) => n + 1)}
      />

      {isUser && creatorInfo?.rol === ROLE.CREATOR_SUB && (
        <SubscribeModalLoggedIn
          creatorInfo={creatorInfo}
          openSignal={subscribeFromChatOpenSignal}
          handleCallback={onRefreshRoomInfo}
          renderTrigger={() => null}
        />
      )}

      <CallRequestModal
        isOpen={showCallRequestModal}
        onClose={() => {
          if (isCallRequestLoading) return;
          setShowCallRequestModal(false);
        }}
        onAddCredits={() => setShowPurchaseCreditsModal(true)}
        creatorInfo={creatorInfo}
        callKind={callRequestKind}
        roomId={roomId}
        isLoading={isCallRequestLoading}
      />
      <CreatorCallRateModal
        isOpen={showCreatorCallRateModal}
        onClose={() => setShowCreatorCallRateModal(false)}
        callKind={callRequestKind}
        roomId={roomId}
        otherUserId={otherUserId}
      />

      <PurchaseCreditsModal
        isOpen={showPurchaseCreditsModal}
        onClose={() => setShowPurchaseCreditsModal(false)}
      />

      {creatorInfo && (
        <TipModal
          isOpen={showTipModal}
          toggle={() => {
            setShowTipModal(false);
            requestAnimationFrame(() => {
              setTimeout(() => openTipModalRef?.current?.focusComposer?.(), 80);
            });
          }}
          item={{ _id: otherUserId }}
          creatorInfo={creatorInfo}
          tip_type={TIP_TYPE.MESSAGES}
          onTipSuccess={onTipSuccessInChat}
          openTipModalRef={openTipModalRef}
        />
      )}
    </>
  );
};

export default ChatActions;
